import {
  appendPendingTurn,
  applyPanelEvent,
  createPanelEventParser,
  interruptTurn,
  removeTurn,
} from '@/components/companion/panel/companion-panel-stream';
import { useCompanionPage } from '@/components/companion/panel/use-companion-page';
import { useOpenCompanionPanel } from '@/components/companion/panel/use-open-companion-panel';
import { useAuth } from '@/contexts/auth-context';
import {
  COMPANION_PANEL_PATH,
  companionPanelKeys,
  lastTurnOf,
  useCompanionPanelConversationQuery,
} from '@/hooks/queries/use-companion-panel-conversation-query';
import { analyticsSuppressionHeaders } from '@/libs/analytics-choice';
import { getSharedRefreshPromise } from '@/libs/auth-guard';
import api from '@/libs/axiosInstance';
import { getFreshAuthorizationHeader } from '@/libs/fresh-authorization';
import { useSuccessFeeLockStore } from '@/stores/success-fee-lock-store';
import {
  ErrorCode,
  type CompanionPanelConversationDto,
  type CompanionPanelLocale,
  type CompanionPanelQuestionRequest,
} from '@fluenceur/types';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

/** Why a question did not start. The question is handed back so nothing typed is lost. */
export interface CompanionPanelAskNotice {
  kind: 'in_progress' | 'plan' | 'error';
  question: string;
}

export interface CompanionPanelAsk {
  ask: (question: string) => void;
  stop: () => void;
  /** A stream is attached, or the last turn still runs (a reload, another tab). */
  isAnswering: boolean;
  notice: CompanionPanelAskNotice | null;
}

/** Marks a question shown before the server named its turn. */
const PENDING_PREFIX = 'pending-';

/** The stream this hook is reading, if any. Replaced, never mutated across questions. */
interface AttachedStream {
  controller: AbortController;
  pendingId: string;
  /** The id events are applied to: the pending one until the `turn` frame names it. */
  turnRef: string;
  turnId: string | null;
  stopQueued: boolean;
}

/**
 * The SSE stream cannot go through axios, so it carries axios's headers itself
 * and retries once after refreshing the session, as the interceptor would.
 */
async function postQuestion(
  body: CompanionPanelQuestionRequest,
  signal: AbortSignal
): Promise<Response> {
  const send = (authorization: string | null) =>
    fetch(`${api.defaults.baseURL}${COMPANION_PANEL_PATH}/questions`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'text/event-stream',
        'x-product-analytics-surface': 'dashboard',
        ...analyticsSuppressionHeaders(),
        ...(authorization ? { Authorization: authorization } : {}),
      },
      body: JSON.stringify(body),
      signal,
    });

  const response = await send(await getFreshAuthorizationHeader());
  if (response.status !== 401) return response;
  const refreshed = await getSharedRefreshPromise();
  return refreshed.success ? send(await getFreshAuthorizationHeader()) : response;
}

const readEnvelope = async (
  response: Response
): Promise<{ code?: unknown; data?: { deadline?: unknown } }> => {
  try {
    return await response.json();
  } catch {
    return {};
  }
};

/**
 * Asks the Companion and follows the answer. Every streamed event is written
 * into the conversation query cache, which the thread renders from; when the
 * stream drops, the conversation query polls the persisted turn until it
 * settles. Stopping asks the server to stop the turn, and an unmount or a brand
 * switch only stops reading: the answer keeps being written server-side.
 */
export function useCompanionPanelAsk(): CompanionPanelAsk {
  const { user } = useAuth();
  const brandId = user?.currentBrandId ?? '';
  const { i18n } = useTranslation();
  const page = useCompanionPage();
  const { close } = useOpenCompanionPanel();
  const queryClient = useQueryClient();

  const [streamAttached, setStreamAttached] = useState(false);
  const [notice, setNotice] = useState<CompanionPanelAskNotice | null>(null);
  const streamRef = useRef<AttachedStream | null>(null);

  const { data: conversation } = useCompanionPanelConversationQuery(brandId, {
    pollWhileRunning: !streamAttached,
  });
  const lastTurn = lastTurnOf(conversation);
  const isAnswering = streamAttached || lastTurn?.status === 'running';
  const isAnsweringRef = useRef(isAnswering);
  isAnsweringRef.current = isAnswering;

  const { mutate: stopTurn } = useMutation<unknown, Error, string>({
    mutationFn: (turnId) => api.post(`${COMPANION_PANEL_PATH}/turns/${turnId}/stop`),
  });

  // A brand switch or an unmount lets go of the reader, never of the turn.
  useEffect(() => {
    setNotice(null);
    return () => {
      streamRef.current?.controller.abort();
      streamRef.current = null;
      setStreamAttached(false);
    };
  }, [brandId]);

  const ask = useCallback(
    (question: string) => {
      if (streamRef.current || isAnsweringRef.current || !brandId) return;

      const key = companionPanelKeys.conversation(brandId);
      const pendingId = `${PENDING_PREFIX}${Date.now()}`;
      const stream: AttachedStream = {
        controller: new AbortController(),
        pendingId,
        turnRef: pendingId,
        turnId: null,
        stopQueued: false,
      };
      const isCurrent = () => streamRef.current === stream;
      const update = (
        fold: (conversation: CompanionPanelConversationDto) => CompanionPanelConversationDto | null
      ) =>
        queryClient.setQueryData<CompanionPanelConversationDto | null>(key, (current) =>
          current ? fold(current) : current
        );

      streamRef.current = stream;
      setNotice(null);
      setStreamAttached(true);
      void queryClient.cancelQueries({ queryKey: key });
      queryClient.setQueryData<CompanionPanelConversationDto | null>(key, (current) =>
        appendPendingTurn(current, {
          pendingId,
          question,
          pageId: page.id,
          now: new Date().toISOString(),
        })
      );

      let refused = false;
      const refuse = (kind: CompanionPanelAskNotice['kind'] | null) => {
        refused = true;
        update((current) => {
          const next = removeTurn(current, pendingId);
          return next.turns.length === 0 && next.id === pendingId ? null : next;
        });
        if (kind) setNotice({ kind, question });
      };

      const locale: CompanionPanelLocale = i18n?.language?.startsWith('en') ? 'en' : 'fr';
      const body: CompanionPanelQuestionRequest = {
        question,
        pageId: page.id,
        pageFilters: page.filters,
        locale,
      };

      const read = async () => {
        let response: Response;
        try {
          response = await postQuestion(body, stream.controller.signal);
        } catch {
          if (!stream.controller.signal.aborted) refuse('error');
          return;
        }

        if (!response.ok) {
          const envelope = await readEnvelope(response);
          if (envelope.code === ErrorCode.E_COMPANION_PANEL_UNAVAILABLE) {
            // Closed for the organization: the refetch answers it and hides the trigger.
            refuse(null);
            close();
          } else if (envelope.code === ErrorCode.E_COMPANION_PANEL_TURN_IN_PROGRESS) {
            refuse('in_progress');
          } else if (response.status === 423) {
            refuse(null);
            const deadline = envelope.data?.deadline;
            useSuccessFeeLockStore.getState().lock(typeof deadline === 'string' ? deadline : null);
          } else if (response.status === 402) {
            // The billing decision (paywall, upsell, grace banner) is axios's:
            // the conversation read meets the same gate and its interceptor acts on it.
            refuse('plan');
          } else {
            refuse('error');
          }
          return;
        }
        if (!response.body) return;

        const reader = response.body.getReader();
        stream.controller.signal.addEventListener('abort', () => {
          reader.cancel().catch(() => undefined);
        });
        const decoder = new TextDecoder();
        const parser = createPanelEventParser((event) => {
          if (!isCurrent()) return;
          update((current) =>
            applyPanelEvent(current, stream.turnRef, event, new Date().toISOString())
          );
          if (event.type !== 'turn') return;
          stream.turnRef = event.turnId;
          stream.turnId = event.turnId;
          if (stream.stopQueued) stopTurn(event.turnId);
        });

        try {
          for (;;) {
            const { done, value } = await reader.read();
            if (done) break;
            parser.push(decoder.decode(value, { stream: true }));
          }
          parser.push(decoder.decode());
          parser.flush();
        } catch {
          // Dropped (sleep, deploy): the turn goes on server-side, the query polls it.
        }
      };

      void read().finally(() => {
        if (!isCurrent()) return;
        streamRef.current = null;
        setStreamAttached(false);
        // Never named by the server: nothing to poll, so it must not look like it still runs.
        // The refetch below replaces it with the real turn when there is one.
        if (!refused && !stream.turnId) update((current) => interruptTurn(current, pendingId));
        void queryClient.invalidateQueries({ queryKey: key });
      });
    },
    [brandId, close, i18n, page.filters, page.id, queryClient, stopTurn]
  );

  const stop = useCallback(() => {
    const stream = streamRef.current;
    if (stream) {
      if (stream.turnId) stopTurn(stream.turnId);
      else stream.stopQueued = true;
      return;
    }
    // A pending id is not a turn the server knows.
    if (lastTurn?.status === 'running' && !lastTurn.id.startsWith(PENDING_PREFIX)) {
      stopTurn(lastTurn.id);
    }
  }, [lastTurn, stopTurn]);

  return { ask, stop, isAnswering, notice };
}
